import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

// Idle timeout config — enterprise policy
export const IDLE_WARN_SECONDS = 60;          // Show modal 60s before hard logout
export const IDLE_TIMEOUT_SECONDS = 30 * 60;  // Hard 30 minutes total

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smin_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [pendingStage, setPendingStage] = useState(() => localStorage.getItem("smin_pending_stage") || null);
  // pendingStage: 'require_challenge' | 'require_enrollment' | null
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(IDLE_TIMEOUT_SECONDS);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const token = localStorage.getItem("smin_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      localStorage.setItem("smin_user", JSON.stringify(r.data));
    }).catch(() => {
      localStorage.removeItem("smin_token");
      localStorage.removeItem("smin_user");
      setUser(null);
    }).finally(() => setLoading(false));
  }, []);

  // ---------- Idle timeout tracking ----------
  useEffect(() => {
    if (!user) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "pointermove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const tick = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const secondsLeft = Math.max(0, IDLE_TIMEOUT_SECONDS - Math.floor(idleMs / 1000));
      setIdleSecondsLeft(secondsLeft);
      if (secondsLeft <= 0) {
        // Hard logout
        logout({ reason: "idle_timeout" });
      }
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(tick);
    };
  }, [user]);

  const stayActive = () => {
    lastActivityRef.current = Date.now();
    setIdleSecondsLeft(IDLE_TIMEOUT_SECONDS);
  };

  const finalizeLogin = (data) => {
    localStorage.setItem("smin_token", data.token);
    localStorage.setItem("smin_user", JSON.stringify(data.user));
    localStorage.removeItem("smin_pre_token");
    localStorage.removeItem("smin_pending_stage");
    setPendingStage(null);
    setUser(data.user);
    lastActivityRef.current = Date.now();
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // 2FA removed in Iter 17 — always finalize the session immediately.
    finalizeLogin(data);
    return data.user;
  };

  const register = async (name, email, password, extras = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...extras });
    // Iter 72 · Feb 2026 — every new account lands in `pending_approval`.
    // We deliberately DO NOT call finalizeLogin() in that case, so the
    // JWT never touches localStorage and the SignupPage can render the
    // dedicated "awaiting super-admin approval" screen instead of
    // dumping the user into a dashboard that will immediately 403 them.
    if (data.token && data?.user?.status !== "pending_approval") {
      finalizeLogin(data);
    }
    return data.user || data;
  };

  const logout = (opts = {}) => {
    if (localStorage.getItem("smin_token")) {
      // best-effort audit log — fire-and-forget
      api.post("/auth/logout").catch(() => {});
    }
    ["smin_token", "smin_user", "smin_pre_token", "smin_pending_stage"].forEach(
      (k) => localStorage.removeItem(k)
    );
    setUser(null);
    setPendingStage(null);
    const target = opts.reason === "idle_timeout" ? "/login?reason=idle" : "/login";
    window.location.href = target;
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("smin_user", JSON.stringify(data));
    return data;
  };

  return (
    <AuthCtx.Provider value={{
      user, loading, login, register, logout, refresh, finalizeLogin,
      pendingStage, idleSecondsLeft, stayActive,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
